import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2, Rocket, PartyPopper, BookOpen, Users, Presentation, Award, Handshake, TrendingUp, Smartphone, Apple } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/site";
import flyer from "@/assets/turnaround.jpg";

const empty = {
  full_name: "", email: "", phone: "", organization: "", role: "",
  city: "", state: "", youth_count: "", message: "",
};

const ORG_TYPES = ["School", "Church", "Youth Program", "Community Organization", "Nonprofit", "Parent / Guardian", "Other"];

const STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];

const INCLUDES = [
  { icon: BookOpen, t: "Facilitator Guide", d: "Step-by-step 12-week lesson plans" },
  { icon: Users, t: "Student Workbook", d: "Interactive activities & reflections" },
  { icon: Presentation, t: "PowerPoint Slides", d: "Ready-to-use for every lesson" },
  { icon: Award, t: "Certificates & Badges", d: "Celebrate progress & achievement" },
  { icon: Handshake, t: "Community Partnership Guide", d: "Partner with law enforcement & schools" },
  { icon: TrendingUp, t: "Mentor & Parent Portals", d: "Resources, training & progress tracking" },
];

export const Turnaround = () => {
  const [form, setForm] = useState(empty);
  const [orgType, setOrgType] = useState("");
  const [method, setMethod] = useState("cashapp");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.city || !form.state || !orgType) {
      toast.error("Please fill in your name, email, phone, city, state, and organization type.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/toolkit", { ...form, org_type: orgType, payment_method: method });
      setDone(true);
      toast.success("Request received!");
      setForm(empty);
      setOrgType("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="turnaround" data-testid="turnaround-section" className="relative py-24 sm:py-32 px-6 sm:px-10 bg-[#0A0612] border-y border-[#D4AF37]/15">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-14"
        >
          <p className="text-sm uppercase tracking-[0.3em] text-[#9B6DFF] mb-5 flex items-center justify-center gap-2">
            <Rocket className="h-4 w-4" /> Change Center Mentoring
          </p>
          <h2 className="font-display text-4xl sm:text-6xl font-semibold text-[#F9F6F0]">
            The <span className="gold-text italic">Turnaround</span> Project
          </h2>
          <p className="text-[#F9F6F0] text-lg mt-4 font-semibold uppercase tracking-wide">Stop The Chase Before It Starts.</p>
          <p className="text-[#A19CA8] text-lg mt-4 max-w-2xl mx-auto leading-relaxed">
            A powerful 12-week mentoring program with <span className="text-[#D4AF37]">Coach Kaf</span> — designed to help
            young men make better choices, avoid the consequences, and build a stronger future. Request the Turnaround
            Toolkit ($49) for your school, church, youth program, or community, anywhere in the country.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="border border-[#9B6DFF]/30 overflow-hidden mb-8 shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
              <img src={flyer} alt="The Turnaround Project with Coach Kaf" className="w-full h-auto" data-testid="turnaround-flyer" />
            </div>
            <h3 className="font-display text-2xl text-[#D4AF37] mb-5">The Toolkit Includes</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {INCLUDES.map((i) => (
                <div key={i.t} className="flex gap-3">
                  <i.icon className="h-6 w-6 text-[#9B6DFF] shrink-0" />
                  <div>
                    <p className="text-[#F9F6F0] font-semibold text-sm">{i.t}</p>
                    <p className="text-[#A19CA8] text-sm">{i.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-[#130A1F]/70 border border-[#D4AF37]/25 p-7 sm:p-9 lg:sticky lg:top-28"
          >
            {done ? (
              <div className="text-center py-16" data-testid="toolkit-success">
                <PartyPopper className="h-14 w-14 text-[#D4AF37] mx-auto mb-6" />
                <h3 className="font-display text-3xl text-[#F9F6F0] mb-3">Request Received!</h3>
                <p className="text-[#A19CA8] mb-6 max-w-md mx-auto">
                  Thank you for joining The Turnaround. Please send your <span className="text-[#D4AF37]">$49</span> payment to secure your toolkit:
                </p>
                <div className="max-w-xs mx-auto mb-8 border border-[#D4AF37]/40 p-4">
                  {method === "applepay" ? (
                    <>
                      <p className="text-xs uppercase tracking-widest text-[#A19CA8]">Apple Pay</p>
                      <p className="font-display text-2xl gold-text font-bold">404-326-0944</p>
                    </>
                  ) : (
                    <>
                      <p className="text-xs uppercase tracking-widest text-[#A19CA8]">CashApp</p>
                      <p className="font-display text-2xl gold-text font-bold">$thechangecenteratl</p>
                    </>
                  )}
                </div>
                <button onClick={() => setDone(false)} data-testid="toolkit-another-btn" className="border-2 border-[#D4AF37] text-[#D4AF37] uppercase tracking-wider text-sm px-7 py-3 hover:bg-[#D4AF37]/10 transition-all">
                  Submit Another
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="toolkit-form">
                <h3 className="font-display text-2xl text-[#D4AF37] mb-1">Request The Toolkit — $49</h3>
                <p className="text-[#A19CA8] text-sm mb-2">One-time $49 payment. Available for schools, churches, youth programs & communities nationwide.</p>
                <Field label="Full Name" required>
                  <input data-testid="toolkit-name" value={form.full_name} onChange={update("full_name")} placeholder="Your name" className="ccc-input" />
                </Field>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Email" required>
                    <input data-testid="toolkit-email" type="email" value={form.email} onChange={update("email")} placeholder="you@email.com" className="ccc-input" />
                  </Field>
                  <Field label="Phone" required>
                    <input data-testid="toolkit-phone" value={form.phone} onChange={update("phone")} placeholder="(404) 000-0000" className="ccc-input" />
                  </Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Organization">
                    <input data-testid="toolkit-org" value={form.organization} onChange={update("organization")} placeholder="School / church / program" className="ccc-input" />
                  </Field>
                  <Field label="Organization Type" required>
                    <select data-testid="toolkit-org-type" value={orgType} onChange={(e) => setOrgType(e.target.value)} className="ccc-input">
                      <option value="">Select…</option>
                      {ORG_TYPES.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Your Role / Title">
                  <input data-testid="toolkit-role" value={form.role} onChange={update("role")} placeholder="e.g., Principal, Pastor, Director" className="ccc-input" />
                </Field>
                <div className="grid sm:grid-cols-3 gap-5">
                  <div className="sm:col-span-2">
                    <Field label="City" required>
                      <input data-testid="toolkit-city" value={form.city} onChange={update("city")} placeholder="City" className="ccc-input" />
                    </Field>
                  </div>
                  <Field label="State" required>
                    <select data-testid="toolkit-state" value={form.state} onChange={update("state")} className="ccc-input">
                      <option value="">—</option>
                      {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="How many youth would you serve?">
                  <input data-testid="toolkit-youth" value={form.youth_count} onChange={update("youth_count")} placeholder="e.g., 25" className="ccc-input" />
                </Field>
                <Field label="Anything else? (optional)">
                  <textarea data-testid="toolkit-message" value={form.message} onChange={update("message")} rows={3} placeholder="Tell us about your program or timeline..." className="ccc-input resize-none" />
                </Field>

                <div>
                  <label className="block text-sm uppercase tracking-[0.18em] text-[#D4AF37] font-semibold mb-3">
                    Pay Your $49 Toolkit Fee
                  </label>
                  <div className="grid sm:grid-cols-2 gap-2.5" data-testid="toolkit-payment-methods">
                    <PayOption active={method === "cashapp"} onClick={() => setMethod("cashapp")} icon={Smartphone} title="CashApp" sub="$thechangecenteratl" testid="toolkit-pay-cashapp" />
                    <PayOption active={method === "applepay"} onClick={() => setMethod("applepay")} icon={Apple} title="Apple Pay" sub="404-326-0944" testid="toolkit-pay-applepay" />
                  </div>
                  <p className="text-[#D4AF37] text-xs mt-3 font-semibold uppercase tracking-wide">
                    Payment is due at sign-up to receive your toolkit.
                  </p>
                </div>

                <button type="submit" disabled={loading} data-testid="submit-toolkit-btn"
                  className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#7236AD] to-[#5D2A8E] text-white font-semibold uppercase tracking-wider text-sm px-8 py-4 hover:from-[#8A47C9] hover:to-[#7236AD] transition-all shadow-[0_0_20px_rgba(114,54,173,0.4)] disabled:opacity-60">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Submit Toolkit Request"}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-sm uppercase tracking-[0.18em] text-[#D4AF37] font-semibold mb-2">
      {label} {required && <span className="text-[#D4AF37]">*</span>}
    </label>
    {children}
  </div>
);

const PayOption = ({ active, onClick, icon: Icon, title, sub, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`flex flex-col items-center text-center gap-1.5 px-3 py-4 border transition-all ${active ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-[#D4AF37]/20 hover:border-[#D4AF37]/50"}`}>
    <Icon className={`h-6 w-6 ${active ? "text-[#D4AF37]" : "text-[#A19CA8]"}`} />
    <span className={`text-sm font-semibold ${active ? "text-[#F9F6F0]" : "text-[#A19CA8]"}`}>{title}</span>
    <span className="text-xs text-[#A19CA8]">{sub}</span>
  </button>
);
